import React from "react";
import { Routes, Route } from "react-router-dom";
import HomePage from "./HomePage";
import AustraliaMigrationEligibilityChecker from "./AustraliaMigrationEligibilityChecker";
import ContactPage from "./ContactPage";
import PrivacyPolicyPage from "./PrivacyPolicyPage";
import TermsOfServicePage from "./TermsOfServicePage";
import CookieSettingsPage from "./CookieSettingsPage";
import ServicesPage from "./ServicesPage";
import AboutPage from "./AboutPage";
import SkilledVisasAustraliaPage from "./SkilledVisasAustraliaPage";
import AustraliaSkilledIndependentVisa189 from "./pages/australia-skilled-independent-visa-189";
import AustraliaSkilledNominatedVisa190 from "./pages/australia-skilled-nominated-visa-190";
import AustraliaSkilledWorkRegionalVisa491 from "./pages/australia-skilled-work-regional-visa-491";
import AustraliaSkilledRegionalVisa191 from "./pages/australia-skilled-regional-pr-visa-191";
import AustraliaEmployerSponsoredVisas from "./pages/australia-employer-sponsored-visas";
import AustraliaENSVisa186 from "./pages/australia-ens-visa-186";
import AustraliaTSSVisa482 from "./pages/australia-tss-visa-482";
import AustraliaRegionalEmployerSponsoredVisa494 from "./pages/australia-regional-employer-sponsored-visa-494";
import AustraliaSkillsAssessment from "./pages/australia-skills-assessment";
import EmployerSponsoredVisasAustralia from "./pages/employer-sponsored-visas-australia";
import FamilyPartnerVisasAustralia from "./pages/family-partner-visas-australia";
import VisitorTouristVisasAustralia from "./pages/visitor-tourist-visas-australia";
import CorporateImmigrationServicesAustralia from "./pages/corporate-immigration-services-australia";
import AdminPortal from "./admin/AdminPortal";
import ScrollToTop from "./components/ScrollToTop";
import MaintenancePage from "./components/MaintenancePage";
import { useMaintenanceMode } from "./hooks/useMaintenanceMode";
import { retryPendingLeads } from "./admin/api";
import { initCtaTracking } from "./services/crmLeadService";

const App = () => {
  const isAdminDomain = window.location.hostname === 'admin.holaworld.com.au';
  const { maintenanceMode, checked } = useMaintenanceMode(isAdminDomain);

  // On app boot, replay any leads that were queued in localStorage but never
  // acknowledged by the server (e.g. user submitted while offline / network blip).
  React.useEffect(() => {
    if (isAdminDomain) return;
    initCtaTracking();
    // Defer slightly so it doesn't compete with first paint.
    const t = setTimeout(() => { retryPendingLeads(); }, 1500);
    return () => clearTimeout(t);
  }, [isAdminDomain]);

  // Admin portal — only accessible via admin.holaworld.com.au
  if (isAdminDomain) {
    return <AdminPortal />;
  }

  if (!checked) {
    return (
      <div className="min-h-screen bg-[#f6f5f3]" aria-busy="true" aria-label="Loading" />
    );
  }

  if (maintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-settings" element={<CookieSettingsPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/australia-migration-eligibility-checker" element={<AustraliaMigrationEligibilityChecker />} />
        <Route path="/skilled-visas-australia" element={<SkilledVisasAustraliaPage />} />
        <Route path="/australia-skilled-independent-visa-189" element={<AustraliaSkilledIndependentVisa189 />} />
        <Route path="/australia-skilled-nominated-visa-190" element={<AustraliaSkilledNominatedVisa190 />} />
        <Route path="/australia-skilled-work-regional-visa-491" element={<AustraliaSkilledWorkRegionalVisa491 />} />
        <Route path="/australia-skilled-regional-pr-visa-191" element={<AustraliaSkilledRegionalVisa191 />} />
        <Route path="/australia-employer-sponsored-visas" element={<AustraliaEmployerSponsoredVisas />} />
        <Route path="/australia-ens-visa-186" element={<AustraliaENSVisa186 />} />
        <Route path="/australia-tss-visa-482" element={<AustraliaTSSVisa482 />} />
        <Route path="/australia-regional-employer-sponsored-visa-494" element={<AustraliaRegionalEmployerSponsoredVisa494 />} />
        <Route path="/australia-skills-assessment" element={<AustraliaSkillsAssessment />} />
        <Route path="/visitor-tourist-visas-australia" element={<VisitorTouristVisasAustralia />} />
        <Route path="/corporate-immigration-services-australia" element={<CorporateImmigrationServicesAustralia />} />
        <Route path="/employer-sponsored-visas-australia" element={<EmployerSponsoredVisasAustralia />} />
        <Route path="/family-partner-visas-australia" element={<FamilyPartnerVisasAustralia />} />
        {/* Catch-all: show home page */}
        <Route path="*" element={<HomePage />} />
      </Routes>
    </>
  );
};

export default App;